import React, { useCallback, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { CloudUpload, Description, Close } from '@mui/icons-material';
import { MAX_FILE_SIZE, ACCEPTED_FILE_TYPES, STYLE_GUIDE_CONSTRAINTS } from '../../utils/constants';
import { isValidFileSize, isValidPPTFile, isValidStyleGuideFile, isValidStyleGuideSize } from '../../utils/validation';

interface FileUploaderProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  onStyleGuideSelect: (file: File | null) => void;
  selectedStyleGuide: File | null;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  selectedFile,
  onStyleGuideSelect,
  selectedStyleGuide,
}) => {
  const [pptError, setPptError] = useState<string | null>(null);
  const [styleGuideError, setStyleGuideError] = useState<string | null>(null);
  const [pptDragActive, setPptDragActive] = useState(false);
  const [sgDragActive, setSgDragActive] = useState(false);

  const handlePptValidation = useCallback((file: File): boolean => {
    setPptError(null);
    if (!isValidPPTFile(file)) {
      setPptError(`Invalid file type. Please upload a ${ACCEPTED_FILE_TYPES.join(', ')} file.`);
      return false;
    }
    if (!isValidFileSize(file)) {
      setPptError(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`);
      return false;
    }
    return true;
  }, []);

  const handleStyleGuideValidation = useCallback((file: File): boolean => {
    setStyleGuideError(null);
    if (!isValidStyleGuideFile(file)) {
      setStyleGuideError(`Unsupported format. Accepted: ${STYLE_GUIDE_CONSTRAINTS.ACCEPTED_EXTENSIONS.join(', ')}`);
      return false;
    }
    if (!isValidStyleGuideSize(file)) {
      setStyleGuideError(`File size exceeds ${STYLE_GUIDE_CONSTRAINTS.MAX_SIZE_MB}MB limit.`);
      return false;
    }
    return true;
  }, []);

  const handlePptChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && handlePptValidation(file)) {
      onFileSelect(file);
    }
  }, [handlePptValidation, onFileSelect]);

  const handleStyleGuideChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && handleStyleGuideValidation(file)) {
      onStyleGuideSelect(file);
    }
  }, [handleStyleGuideValidation, onStyleGuideSelect]);

  const handlePptDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setPptDragActive(true);
    } else if (e.type === 'dragleave') {
      setPptDragActive(false);
    }
  }, []);

  const handlePptDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPptDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && handlePptValidation(file)) {
      onFileSelect(file);
    }
  }, [handlePptValidation, onFileSelect]);

  const handleSgDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setSgDragActive(true);
    } else if (e.type === 'dragleave') {
      setSgDragActive(false);
    }
  }, []);

  const handleSgDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSgDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && handleStyleGuideValidation(file)) {
      onStyleGuideSelect(file);
    }
  }, [handleStyleGuideValidation, onStyleGuideSelect]);

  return (
    <Box>
      {/* PowerPoint File (Required) */}
      <Paper
        onDragEnter={handlePptDrag}
        onDragLeave={handlePptDrag}
        onDragOver={handlePptDrag}
        onDrop={handlePptDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: pptDragActive ? 'primary.main' : 'divider',
          bgcolor: pptDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {selectedFile ? selectedFile.name : 'Upload PowerPoint File'}
        </Typography>

        <Chip label="Required" size="small" color="primary" sx={{ mb: 1 }} />

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Drag and drop your file here, or click to browse
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" mb={3}>
          Supported formats: {ACCEPTED_FILE_TYPES.join(', ')} (Max {MAX_FILE_SIZE / 1024 / 1024}MB)
        </Typography>

        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUpload />}
        >
          {selectedFile ? 'Change File' : 'Select File'}
          <input
            type="file"
            hidden
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={handlePptChange}
          />
        </Button>
      </Paper>

      {pptError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {pptError}
        </Alert>
      )}

      {/* Style Guide (Optional) */}
      <Paper
        onDragEnter={handleSgDrag}
        onDragLeave={handleSgDrag}
        onDragOver={handleSgDrag}
        onDrop={handleSgDrop}
        sx={{
          p: 3,
          mt: 3,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: selectedStyleGuide
            ? 'success.main'
            : sgDragActive
              ? 'secondary.main'
              : 'divider',
          bgcolor: sgDragActive ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'secondary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        {selectedStyleGuide ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <Description color="success" />
            <Typography variant="body1" fontWeight={500}>
              {selectedStyleGuide.name}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onStyleGuideSelect(null);
                setStyleGuideError(null);
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <Description sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" gutterBottom>
              Upload Style Guide
            </Typography>
          </>
        )}

        <Chip label="Optional" size="small" variant="outlined" sx={{ mb: 1 }} />

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Upload a style guide to check slides for brand adherence
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Supported formats: PDF, DOCX, TXT, images, and more (Max {STYLE_GUIDE_CONSTRAINTS.MAX_SIZE_MB}MB)
        </Typography>

        <Button
          variant="outlined"
          component="label"
          size="small"
          startIcon={<Description />}
        >
          {selectedStyleGuide ? 'Change Style Guide' : 'Select Style Guide'}
          <input
            type="file"
            hidden
            accept={STYLE_GUIDE_CONSTRAINTS.ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleStyleGuideChange}
          />
        </Button>
      </Paper>

      {styleGuideError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {styleGuideError}
        </Alert>
      )}
    </Box>
  );
};
